import { createSelector } from '@reduxjs/toolkit';
// import { call, all, put, takeEvery } from 'redux-saga/effects';
import { createApi } from '@reduxjs/toolkit/query/react';
import { Task } from './models';
import { baseApi } from '@centerpiece/components';
import { project } from './project.api';

export const task = baseApi
  .enhanceEndpoints({ addTagTypes: ['Tasks', 'Task'] })
  .injectEndpoints({
    endpoints: builder => ({
      getTasks: builder.query<Task[], { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: 'task',
          method: 'GET',
          params: params ? params : { rollup: 'all' },
        }),
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({ type: 'Task' as const, id })),
                'Tasks',
              ]
            : ['Tasks'],
      }),
      getTask: builder.query<Task, { id: number; params?: object }>({
        query: ({ id, params }: { id: number; params?: object }) => ({
          url: `task/${id}`,
          method: 'GET',
          params: params,
        }),
        providesTags: (result, error, arg) => [{ type: 'Task', id: arg.id }],
        // configuration for an individual endpoint, overriding the api setting
        keepUnusedDataFor: 90,
      }),
      createTask: builder.mutation({
        query: request => ({
          url: 'task',
          method: 'POST',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => {
          project.util.invalidateTags([
            'Projects',
            { type: 'Project', id: arg.project },
          ]);
          return ['Tasks'];
        },
      }),
      patchTask: builder.mutation<Task, Task>({
        query: request => ({
          url: `task/${request.id}`,
          method: 'PATCH',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => {
          return ['Tasks', { type: 'Task', id: arg.id }];
        },
        async onQueryStarted(
          { id, project: pid, ...patch },
          { dispatch, queryFulfilled }
        ) {
          const patchResult = dispatch(
            task.util.updateQueryData(
              'getTask',
              { id, params: { nested: 'all' } },
              draft => {
                Object.assign(draft, patch);
              }
            )
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();
            /**
             * Alternatively, on failure you can invalidate the corresponding cache tags
             * to trigger a re-fetch:
             * dispatch(task.util.invalidateTags(['Tasks']))
             */
            dispatch(task.util.invalidateTags([{ type: 'Task', id: id }]));
          }
        },
      }),
      updateTask: builder.mutation<Task, Task>({
        query: request => ({
          url: `task/${request.id}`,
          method: 'PUT',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => {
          return ['Tasks', { type: 'Task', id: arg.id }];
        },
        async onQueryStarted(
          { id, project: pid, ...patch },
          { dispatch, queryFulfilled }
        ) {
          const patchResult = dispatch(
            task.util.updateQueryData(
              'getTask',
              { id, params: { nested: 'all' } },
              draft => {
                Object.assign(draft, patch);
              }
            )
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();
            /**
             * Alternatively, on failure you can invalidate the corresponding cache tags
             * to trigger a re-fetch:
             * dispatch(task.util.invalidateTags(['Tasks']))
             */
            dispatch(task.util.invalidateTags([{ type: 'Task', id: id }]));
          }
        },
      }),
      deleteTask: builder.mutation<Task, number>({
        query: id => ({
          url: `task/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, arg) =>
          result ? [{ type: 'Task' as const, arg }, 'Tasks'] : ['Tasks'],
      }),
    }),
  });

// Auto-generated hooks
export const {
  usePatchTaskMutation,
  useGetTasksQuery,
  useGetTaskQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useDeleteTaskMutation,
  useLazyGetTaskQuery,
  usePrefetch: useTasksPrefetch,
} = task;
const selectTasksRoot = task.endpoints.getTasks.select({});
export const selectTasks = createSelector(
  selectTasksRoot,
  task => task.data || []
);

export const subscribeTask = task.endpoints.getTask.useQueryState;
